import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from "@heroui/react";
import { useState } from "react";

import { errorMessage } from "@/lib/api";
import { createProcess, type ProcessInput } from "./api";
import { PROCESS_FIELDS } from "./columns";

/**
 * 初期値。費目のような「未設定に戻せない選択項目」は最初から選んだ状態にする
 * （空の Select を出すと、選ばないまま登録して既定値が入ったことに気付けない）。
 */
function initialValues(): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of PROCESS_FIELDS) {
    if (field.defaultValue) values[field.key] = field.defaultValue;
  }
  return values;
}

/**
 * 工程の新規登録ダイアログ。
 *
 * 入力項目は一覧の列定義（[[PROCESS_FIELDS]]）から組み立てる。列を足したら
 * 登録フォームにも自動で出るようにして、二重管理を避ける（取引先と同じ作り）。
 *
 * 工程コード・工程名の重複（409）はダイアログを閉じずにその場で出す。入力し直せば
 * 解決する失敗のため。
 */
export default function ProcessCreateDialog({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (processName: string) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setValues(initialValues());
    setError(null);
    setSaving(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    const missing = PROCESS_FIELDS.filter((f) => f.required && !values[f.key]?.trim());
    if (missing.length > 0) {
      setError(`${missing.map((f) => f.label).join("・")}は必須です。`);
      return;
    }

    const price = values.base_price?.trim();
    if (price && !Number.isFinite(Number(price))) {
      setError("時間単価は数値で入力してください。");
      return;
    }

    const input: ProcessInput = {};
    for (const field of PROCESS_FIELDS) {
      const value = values[field.key]?.trim();
      // 任意項目の空欄は送らない（API 側で未入力として扱われる）。
      if (value) (input as Record<string, string>)[field.key] = value;
    }

    setSaving(true);
    try {
      const created = await createProcess(input);
      onCreated(created.process_name);
      close();
    } catch (e) {
      setError(errorMessage(e, "登録に失敗しました。"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && close()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>工程の新規登録</span>
          <span className="text-small font-normal text-default-500">
            工程コードと工程名は必須です。費目は、客先への回答を費目別に集計するための区分です（迷ったら「その他」のままで構いません）。時間単価は見積で時間を入れたときの既定値で、外注費など時間で見積らない工程は空のままで構いません。
          </span>
        </ModalHeader>

        <ModalBody>
          {/* 補足の有無で項目の高さが変わるため、上端で揃える（既定の stretch
              だと背の低い項目が縦中央に寄ってラベルの位置がずれる）。 */}
          <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
            {PROCESS_FIELDS.map((field) =>
              field.kind === "select" ? (
                <Select
                  key={field.key}
                  label={field.label}
                  aria-label={field.label}
                  labelPlacement="outside"
                  variant="bordered"
                  radius="sm"
                  disallowEmptySelection
                  isRequired={field.required}
                  selectedKeys={values[field.key] ? [values[field.key]] : []}
                  onSelectionChange={(keys) => {
                    const key = [...keys][0];
                    if (key == null) return;
                    setValues((prev) => ({ ...prev, [field.key]: String(key) }));
                    setError(null);
                  }}
                >
                  {(field.options ?? []).map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
              ) : (
                <Input
                  key={field.key}
                  label={field.label}
                  aria-label={field.label}
                  labelPlacement="outside"
                  placeholder=" "
                  variant="bordered"
                  radius="sm"
                  type={field.kind === "number" ? "number" : "text"}
                  step={field.kind === "number" ? "0.01" : undefined}
                  isRequired={field.required}
                  description={field.kind === "number" ? "円／時間" : undefined}
                  value={values[field.key] ?? ""}
                  onValueChange={(v) => {
                    setValues((prev) => ({ ...prev, [field.key]: v }));
                    setError(null);
                  }}
                />
              ),
            )}
          </div>
          {error && <p className="mt-2 text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={close}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="create-submit"
            isLoading={saving}
            onPress={() => void submit()}
          >
            登録
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
